import React, { useEffect, useMemo, useRef, useState } from "react";

// Reuse existing helpers
import ExportMenu from "../components/ExportMenu.jsx";
import { exportTable } from "../lib/csv.js";

// ---- constants
const CAMS = [
  { id: "41", name: "41" },
  { id: "cheltenham", name: "Cheltenham" },
  { id: "pool", name: "Pool" },
];
const DEFAULT_LIMIT = 20;

// ---- inline theme (scoped to this file)
const C = {
  bgCard: "#0d1219",
  bdCard: "#1d2633",
  text: "#e6edf6",
  textMuted: "#a3b1c2",
  ghost: "#121a24",
  ghostBd: "#233044",
  ghostHover: "#0f1722",
  primary: "#2563eb",
  danger: "#ef4444",
  tableHead: "#0f1620",
  chipBg: "#1b2431",
};

const pageWrap = { maxWidth: 1200, margin: "0 auto", padding: "20px 16px" };
const topBar = { display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, marginBottom: 16 };
const leftBar = { display: "flex", alignItems: "center", gap: 12 };
const h1Style = { fontSize: 26, fontWeight: 700, textAlign: "center", margin: "8px 0 22px", color: C.text };

const tilesGrid = { display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))" };
const tableGrid = { display: "grid", gap: 18, gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", marginTop: 24 };

const card = {
  background: C.bgCard,
  border: `1px solid ${C.bdCard}`,
  borderRadius: 14,
  boxShadow: "0 4px 14px rgba(0,0,0,.25)",
  padding: 12,
};
const headerRowCentered = {
  position: "relative",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  paddingBottom: 6,
};
const titleLabel = { fontWeight: 700, letterSpacing: 0.2, color: C.text };

const liveDotAbs = {
  position: "absolute",
  top: 4,
  right: 8,
  width: 10,
  height: 10,
  borderRadius: "50%",
  background: C.danger,
  boxShadow: "0 0 0 0 rgba(239,68,68,.7)",
  animation: "lastseenPulse 1.6s infinite",
};

const ratioWrap = {
  position: "relative",
  paddingTop: "56.25%",
  borderRadius: 12,
  overflow: "hidden",
  background: "#000",
  border: `1px solid ${C.bdCard}`,
};
const ratioInner = { position: "absolute", inset: 0, width: "100%", height: "100%" };

// controls
const controlsRow = {
  display: "flex",
  alignItems: "center",
  justifyContent: "flex-start",
  gap: 10,
  marginBottom: 10,
  flexWrap: "wrap",
  rowGap: 8,
};
const controlsLeft = { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" };
const inputSm = {
  width: 76,
  padding: "6px 10px",
  borderRadius: 10,
  background: C.ghost,
  color: C.text,
  border: `1px solid ${C.bdCard}`,
};
const btnOutlineSm = {
  background: C.ghost,
  border: `1px solid ${C.bdCard}`,
  padding: "6px 10px",
  borderRadius: 10,
  minWidth: 56,
};
const btnPrimarySm = { ...btnOutlineSm, background: C.primary, borderColor: C.primary };
const pagerBlock = { display: "flex", alignItems: "center", gap: 8 };
const counter = {
  fontSize: 13,
  color: C.textMuted,
  background: C.ghost,
  border: `1px solid ${C.bdCard}`,
  borderRadius: 10,
  padding: "6px 8px",
  minWidth: 72,
  textAlign: "center",
};

const tableStyle = { width: "100%", borderCollapse: "collapse", color: C.text };
const th = {
  textAlign: "left",
  background: C.tableHead,
  borderBottom: `1px solid ${C.bdCard}`,
  padding: "8px 10px",
  position: "sticky",
  top: 0,
  zIndex: 1,
};
const td = { padding: "8px 10px", borderBottom: `1px solid ${C.bdCard}` };

const chip = {
  display: "inline-block",
  background: C.chipBg,
  border: `1px solid ${C.bdCard}`,
  borderRadius: 8,
  padding: "4px 8px",
  fontWeight: 700,
  letterSpacing: 0.3,
  cursor: "pointer",
  color: "#cfe1ff",
};

// ---- small atoms (define BEFORE use)
function LivePill({ active }) {
  const pill = {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "6px 12px",
    borderRadius: 999,
    fontSize: 13,
    background: active ? "rgba(239,68,68,.16)" : C.ghost,
    border: `1px solid ${active ? "rgba(239,68,68,.35)" : C.bdCard}`,
    color: active ? "#fecaca" : C.textMuted,
    fontWeight: 800,
  };
  const dot = { width: 8, height: 8, borderRadius: "50%", background: active ? C.danger : "#9ca3af" };
  return (
    <div style={pill}>
      <span style={dot} />
      {active ? "LIVE" : "PAUSED"}
    </div>
  );
}

// ---- table column component (define BEFORE use)
function LastSeenColumn({
  cam, limit, onLimitChange, slice, total, offset, onPrev, onNext, onRowClick,
}) {
  const showingStart = Math.min(total, offset + 1);
  const showingEnd = Math.min(total, offset + limit);

  return (
    <div style={card}>
      <h3 style={{ ...titleLabel, textAlign: "center", marginBottom: 8 }}>{cam.name}</h3>

      <div style={controlsRow}>
        <div style={controlsLeft}>
          <label style={{ fontSize: 14, color: C.textMuted }}>Vehicles</label>
          <input
            type="number"
            min={5}
            max={100}
            value={limit}
            onChange={(e) => onLimitChange(Math.max(1, Number(e.target.value || 1)))}
            style={inputSm}
          />
          <button className="btn btn-outline" style={btnOutlineSm} onClick={() => onLimitChange(limit)}>
            Apply
          </button>
        </div>

        <div style={pagerBlock}>
          <button className="btn btn-outline" style={btnOutlineSm} onClick={onPrev} disabled={offset <= 0}>
            Prev
          </button>
          <div style={counter}>{`${showingStart}–${showingEnd} of ${total}`}</div>
          <button className="btn btn-outline" style={btnOutlineSm} onClick={onNext} disabled={offset + limit >= total}>
            Next
          </button>
        </div>
      </div>

      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={th}>Plate</th>
            <th style={th}>Date / Time</th>
          </tr>
        </thead>
        <tbody>
          {slice.map((r, i) => (
            <tr key={`${cam.id}-${offset + i}`}>
              <td style={td}>
                <button onClick={() => onRowClick(r)} title="View images for this sighting" style={chip}>
                  {r.plate}
                </button>
              </td>
              <td style={td}>{formatTime(r.time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

(function ensurePulseKeyframes() {
  if (typeof document === "undefined") return;
  const id = "lastseen-pulse-keyframes";
  if (document.getElementById(id)) return;
  const style = document.createElement("style");
  style.id = id;
  style.textContent = `
    @keyframes lastseenPulse { to { box-shadow: 0 0 0 10px rgba(239,68,68,0); } }
  `;
  document.head.appendChild(style);
})();

// ---- formatting helpers
function pad(n) { return n < 10 ? "0" + n : "" + n; }
function dayName(d) { return ["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][d]; }
function formatTime(ts) {
  const dt = new Date(ts);
  if (isNaN(dt.getTime())) return ts;
  const MM = pad(dt.getMonth() + 1);
  const DD = pad(dt.getDate());
  const YYYY = dt.getFullYear();
  let hh = dt.getHours();
  const mm = pad(dt.getMinutes());
  const ss = pad(dt.getSeconds());
  const am = hh >= 12 ? "PM" : "AM";
  hh = hh % 12; if (hh === 0) hh = 12;
  const dow = dayName(dt.getDay());
  return `${MM}-${DD}-${YYYY} ${hh}:${mm}:${ss} ${am} ${dow}`;
}

export default function LastSeen() {
  const [limit, setLimit] = useState(DEFAULT_LIMIT);
  const [offsets, setOffsets] = useState({ "41": 0, cheltenham: 0, pool: 0 });

  const [rows, setRows] = useState({ "41": [], cheltenham: [], pool: [] });
  const [totals, setTotals] = useState({ "41": 0, cheltenham: 0, pool: 0 });

  const [mode, setMode] = useState("live");
  const isLive = mode === "live";

  const [selected, setSelected] = useState(null); // { plate, time, camera_id, files:{vehicle,background,plate} }

  // initial + SSE
  useEffect(() => {
    const controller = new AbortController();

    const fetchInitial = async () => {
      const q = new URLSearchParams({
        cameras: CAMS.map((c) => c.id).join(","),
        limit: String(Math.max(100, limit)),
      });
      const res = await fetch(`/api/last-seen?${q.toString()}`, { signal: controller.signal });
      if (!res.ok) throw new Error("Failed to load last-seen");
      const data = await res.json();
      setRows((prev) => ({ ...prev, ...data.rows }));
      if (data.totals) setTotals((t) => ({ ...t, ...data.totals }));
    };

    fetchInitial().catch(console.error);

    const q2 = new URLSearchParams({
      cameras: CAMS.map((c) => c.id).join(","),
      limit: String(Math.max(100, limit)),
    });
    const es = new EventSource(`/api/last-seen/stream?${q2.toString()}`);
    es.onmessage = (ev) => {
      try {
        const payload = JSON.parse(ev.data || "{}");
        if (payload.type === "init" && payload.rows) {
          setRows((prev) => ({ ...prev, ...payload.rows }));
          if (payload.totals) setTotals((t) => ({ ...t, ...payload.totals }));
        } else if (payload.type === "update" && payload.camera && payload.rows) {
          setRows((prev) => ({ ...prev, [payload.camera]: payload.rows }));
          if (payload.totals && payload.totals[payload.camera] != null) {
            setTotals((t) => ({ ...t, [payload.camera]: payload.totals[payload.camera] }));
          }
        }
      } catch (e) {
        console.error("SSE parse error", e);
      }
    };
    es.onerror = () => console.warn("SSE error (continuing) last-seen");
    return () => { controller.abort(); es.close(); };
  }, [limit]);

  const pageSlices = useMemo(() => {
    const out = {};
    for (const cam of CAMS) {
      const start = offsets[cam.id] || 0;
      const end = start + limit;
      out[cam.id] = rows[cam.id]?.slice(start, end) ?? [];
    }
    return out;
  }, [rows, offsets, limit]);

  const handlePrev = (camId) => setOffsets((o) => ({ ...o, [camId]: Math.max(0, (o[camId] || 0) - limit) }));
  const handleNext = (camId) => {
    const total = totals[camId] || rows[camId]?.length || 0;
    setOffsets((o) => {
      const next = (o[camId] || 0) + limit;
      return { ...o, [camId]: Math.min(next, Math.max(0, total - limit)) };
    });
  };

  const onRowClick = async (row) => {
    try {
      const id = row?.detection_id ?? row?.id;
      if (!id) return;
      const q = new URLSearchParams({ detection_id: String(id) });
      const res = await fetch(`/api/media/latest?${q.toString()}`);
      if (!res.ok) throw new Error("media fetch failed");
      const data = await res.json();
      if (data?.found && data.files) {
        setSelected({
          plate: data.plate ?? row.plate,
          time: data.time ?? row.time,
          camera_id: data.camera_id ?? row.camera_id,
          files: data.files,
        });
        setMode("media");
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleExport = (type) => {
    const columns = CAMS.map((c) => pageSlices[c.id]);
    const maxLen = Math.max(...columns.map((c) => c.length));
    const headers = CAMS.flatMap((c) => [`Plate (${c.name})`, `Time (${c.name})`]);

    const rowsOut = [];
    for (let i = 0; i < maxLen; i++) {
      const rowObj = {};
      for (let k = 0; k < CAMS.length; k++) {
        const cam = CAMS[k];
        const item = columns[k][i];
        rowObj[`Plate (${cam.name})`] = item?.plate ?? "";
        rowObj[`Time (${cam.name})`] = item?.time ? formatTime(item.time) : "";
      }
      rowsOut.push(rowObj);
    }

    exportTable(type, {
      headers,
      rows: rowsOut,
      title: "Last-Seen Dashboard",
      filenameBase: "last-seen",
    });
  };

  const showLive = () => { setSelected(null); setMode("live"); };

  return (
    <div style={pageWrap}>
      <div style={topBar}>
        <div style={leftBar}>
          <LivePill active={isLive} />
          <button className="btn btn-outline" style={btnOutlineSm} onClick={showLive} disabled={isLive} title="Show Live Feed">
            Show Live Feed
          </button>
        </div>
        <ExportMenu onExport={handleExport} />
      </div>

      <h1 style={h1Style}>Real-Time / Last-Seen Dashboard</h1>

      {mode === "media" && selected?.plate && (
        <div style={{ ...topBar, marginBottom: 10 }}>
          <div style={{ fontSize: 14, color: C.textMuted }}>
            Selected plate: <span style={{ fontWeight: 700, color: C.text }}>{selected.plate}</span>
          </div>
          {(() => {
            const raw = selected?.time || "";
            const day = raw.slice(0, 10);
            let href = `/dev/sightings?plate=${encodeURIComponent(selected.plate)}&exact=1`;
            if (day) {
              const d = new Date(day + "T00:00:00");
              const d2 = new Date(d); d2.setDate(d2.getDate() + 1);
              const fmt = (x) => `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2,"0")}-${String(x.getDate()).padStart(2,"0")}`;
              href += `&start=${fmt(d)}&end=${fmt(d2)}`;
            }
            return (
              <a className="btn btn-primary" style={btnPrimarySm} href={href} target="_self" rel="noreferrer">
                View all sightings for this plate
              </a>
            );
          })()}
        </div>
      )}

      <MediaTiles mode={mode} selected={selected} />

      <div style={tableGrid}>
        {CAMS.map((cam) => (
          <LastSeenColumn
            key={cam.id}
            cam={cam}
            limit={limit}
            onLimitChange={setLimit}
            slice={pageSlices[cam.id]}
            total={totals[cam.id] || rows[cam.id]?.length || 0}
            offset={offsets[cam.id] || 0}
            onPrev={() => handlePrev(cam.id)}
            onNext={() => handleNext(cam.id)}
            onRowClick={onRowClick}
          />
        ))}
      </div>
    </div>
  );
}

function MediaTiles({ mode, selected }) {
  const videoRefs = { "41": useRef(null), cheltenham: useRef(null), pool: useRef(null) };

  // --- HLS fallback (lazy-load from CDN)
  const hlsInstances = useRef({});
  const hlsReadyRef = useRef(null);
  if (!hlsReadyRef.current) {
    hlsReadyRef.current = new Promise((resolve) => {
      if (window.Hls) return resolve(window.Hls);
      const s = document.createElement("script");
      s.src = "https://cdn.jsdelivr.net/npm/hls.js@1.5.8/dist/hls.min.js";
      s.async = true;
      s.onload = () => resolve(window.Hls);
      document.head.appendChild(s);
    });
  }

  // --- WebRTC/WHEP state
  const pcRefs = useRef({});      // { [camId]: RTCPeerConnection }
  const iceUrls = useRef({});     // { [camId]: trickle target URL (from Location header) }

  const getWhepBase = () => {
    const proto = location.protocol.startsWith("https") ? "https" : "http";
    return `${proto}://${location.hostname}:8889`;
  };

  const stopWebRTC = (camId) => {
    const pc = pcRefs.current[camId];
    if (pc) {
      try { pc.getSenders?.().forEach((s) => s.track && s.track.stop()); } catch {}
      try { pc.close(); } catch {}
      delete pcRefs.current[camId];
      delete iceUrls.current[camId];
    }
  };

  const playWhep = async (camId, videoEl) => {
    const endpoint = `${getWhepBase()}/whep/${encodeURIComponent(camId)}`;

    stopWebRTC(camId);
    const pc = new RTCPeerConnection({
      iceServers: [{ urls: ["stun:stun.l.google.com:19302"] }],
    });
    pcRefs.current[camId] = pc;

    pc.addTransceiver("video", { direction: "recvonly" });

    pc.ontrack = (ev) => {
      const [stream] = ev.streams;
      if (videoEl) {
        videoEl.srcObject = stream;
        videoEl.autoplay = true;
        videoEl.muted = true;
        videoEl.playsInline = true;
        videoEl.play().catch(() => {});
      }
    };

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    const resp = await fetch(endpoint, {
      method: "POST",
      headers: { "Content-Type": "application/sdp" },
      body: offer.sdp,
    });
    if (!resp.ok) throw new Error(`WHEP POST failed: ${resp.status}`);

    const answerSDP = await resp.text();
    const loc = resp.headers.get("location");
    if (loc) {
      const absolute = new URL(loc, endpoint).toString();
      iceUrls.current[camId] = absolute;
    }

    await pc.setRemoteDescription({ type: "answer", sdp: answerSDP });

    pc.onicecandidate = async (ev) => {
      const iceUrl = iceUrls.current[camId];
      if (!iceUrl) return;
      try {
        if (ev.candidate && ev.candidate.candidate) {
          await fetch(iceUrl, {
            method: "PATCH",
            headers: { "Content-Type": "application/trickle-ice-sdpfrag" },
            body: `a=${ev.candidate.candidate}`,
          });
        } else if (ev.candidate === null) {
          await fetch(iceUrl, {
            method: "PATCH",
            headers: { "Content-Type": "application/trickle-ice-sdpfrag" },
            body: "a=end-of-candidates",
          });
        }
      } catch {
        // ignore trickle errors; playback may still be fine
      }
    };
  };

  const startHls = async (camId, videoEl) => {
    if (!videoEl) return;
    const src = `/hls/${camId}/index.m3u8`;

    if (videoEl.canPlayType("application/vnd.apple.mpegurl")) {
      videoEl.src = src;
      videoEl.autoplay = true;
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.addEventListener("canplay", () => videoEl.play().catch(() => {}), { once: true });
      await videoEl.play().catch(() => {});
      return;
    }
    const HlsCtor = await hlsReadyRef.current;
    if (HlsCtor && HlsCtor.isSupported()) {
      const hls = new HlsCtor({ enableWorker: true });
      hls.loadSource(src);
      hls.attachMedia(videoEl);
      hls.on(HlsCtor.Events.MANIFEST_PARSED, () => videoEl.play().catch(() => {}));
      hlsInstances.current[camId] = hls;
    } else {
      videoEl.src = src;
      videoEl.autoplay = true;
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.addEventListener("canplay", () => videoEl.play().catch(() => {}), { once: true });
      await videoEl.play().catch(() => {});
    }
  };

  const startLiveForCam = async (camId, videoEl) => {
    if (!videoEl) return;
    try {
      await playWhep(camId, videoEl);
      return; // WebRTC succeeded
    } catch {
      stopWebRTC(camId);
      await startHls(camId, videoEl); // fallback to HLS
    }
  };

  const destroyAll = () => {
    // stop WebRTC
    Object.keys(pcRefs.current).forEach(stopWebRTC);
    // stop HLS
    Object.values(hlsInstances.current).forEach((h) => { try { h.destroy(); } catch {} });
    hlsInstances.current = {};
    // reset videos
    Object.values(videoRefs).forEach((r) => {
      const el = r.current;
      if (!el) return;
      try { el.pause?.(); } catch {}
      try { el.srcObject = null; } catch {}
      el.removeAttribute("src");
      el.load?.();
    });
  };

  useEffect(() => {
    if (mode === "live") {
      for (const cam of CAMS) startLiveForCam(cam.id, videoRefs[cam.id].current);
    } else {
      destroyAll();
    }
    return () => { destroyAll(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  // Lightbox for media view
  const [lightbox, setLightbox] = useState(null);
  const openLightbox = (src, title) => setLightbox({ src, title });
  const closeLightbox = () => setLightbox(null);

  // --- Media mode (images)
  if (mode === "media" && selected?.files) {
    const f = selected.files;
    const tiles = [
      { title: "Vehicle", src: f.vehicle },
      { title: "Background", src: f.background },
      { title: "Plate", src: f.plate },
    ];
    return (
      <>
        <div style={tilesGrid}>
          {tiles.map((t) => (
            <div key={t.title} style={card}>
              <div style={headerRowCentered}>
                <h3 style={titleLabel}>{t.title}</h3>
              </div>
              <div style={ratioWrap}>
                <img
                  alt={t.title}
                  src={t.src}
                  onClick={() => openLightbox(t.src, t.title)}
                  style={{ ...ratioInner, objectFit: "cover", cursor: "zoom-in" }}
                />
              </div>
            </div>
          ))}
        </div>

        {lightbox && (
          <div
            onClick={closeLightbox}
            style={{
              position: "fixed", inset: 0, background: "rgba(0,0,0,.86)",
              display: "flex", alignItems: "center", justifyContent: "center",
              zIndex: 3000, cursor: "zoom-out",
            }}
          >
            <img
              alt={lightbox.title}
              src={lightbox.src}
              style={{ maxWidth: "92vw", maxHeight: "92vh", objectFit: "contain", borderRadius: 8 }}
            />
          </div>
        )}
      </>
    );
  }

  // --- Live mode (video)
  return (
    <div style={tilesGrid}>
      {CAMS.map((c) => (
        <div key={c.id} style={card}>
          <div style={headerRowCentered}>
            <h3 style={titleLabel}>{c.name}</h3>
            <span aria-label="live" style={liveDotAbs} />
          </div>
          <div style={ratioWrap}>
            <video
              ref={videoRefs[c.id]}
              controls
              playsInline
              muted
              autoPlay
              style={ratioInner}
            />
          </div>
        </div>
      ))}
    </div>
  );
}
